import React from 'react';
import {
  Calendar,
  CreditCard,
  FileText,
  Globe,
  Hash,
  Image,
  LayoutTemplate,
  Lock,
  Mail,
  Minus,
  Palette,
  Phone,
  Square,
  Type,
  User,
  Users,
  MapPin,
} from 'lucide-react';
import {
  LAYOUT_FIELD_DEFS,
  type DocumentLayoutV1,
  type LayoutBand,
  type LayoutElementKey,
  type LayoutFieldDef,
} from '../../utils/documentLayoutTypes';
import { SETTINGS_FIELD_LABEL, SETTINGS_INPUT_CLASS } from '../../utils/settingsSectionStyles';

type Props = {
  layout: DocumentLayoutV1;
  activeBand: LayoutBand;
  selectedId: string | null;
  templateName: string;
  onTemplateNameChange: (name: string) => void;
  onBandChange: (band: LayoutBand) => void;
};

/** Labeled fields that may appear more than once (e.g. Rechnungsnr. in Kopf und Meta). */
const MULTI_FIELD_KEYS = new Set<LayoutElementKey>(['invoiceNumber']);

const FIELD_ICONS: Partial<Record<LayoutElementKey, React.ReactNode>> = {
  senderHeadline: <Type className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  recipient: <Users className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  ansprechpartner: <User className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  documentTitle: <FileText className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  documentType: <FileText className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  offerTitle: <Type className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  invoiceNumber: <Hash className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  issueDate: <Calendar className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  dueDate: <Calendar className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  customerNumber: <Hash className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  projectNumber: <Hash className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  orderNumber: <Hash className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  leistungsort: <MapPin className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  leistungszeitraum: <Calendar className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  logo: <Image className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  headerBanner: <LayoutTemplate className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  companyAddress: <MapPin className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  companyPhone: <Phone className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  companyEmail: <Mail className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  companyWebsite: <Globe className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  freeText: <Type className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  divider: <Minus className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  colorBlock: <Square className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  legalBlock: <FileText className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  bankBlock: <CreditCard className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  contactBlock: <User className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  pageNumber: <Hash className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
  signatureImage: <Image className="h-4 w-4 shrink-0" strokeWidth={1.75} />,
};

/** One palette entry per design key (no Kopf/Fuß duplicates). */
const DESIGN_DEFS: LayoutFieldDef[] = (() => {
  const seen = new Set<LayoutElementKey>();
  const out: LayoutFieldDef[] = [];
  for (const d of LAYOUT_FIELD_DEFS) {
    if (d.kind !== 'design' || seen.has(d.key)) continue;
    seen.add(d.key);
    out.push(d);
  }
  return out;
})();

function navBtnClass(active: boolean) {
  return `w-full flex items-center gap-3 px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
    active
      ? 'bg-app-surface-muted text-app-text font-medium [&>svg]:text-app-text'
      : 'text-app-text-muted font-normal hover:bg-app-hover [&>svg]:text-slate-400'
  }`;
}

function bandsForKey(key: LayoutElementKey): LayoutBand[] {
  return LAYOUT_FIELD_DEFS.filter((d) => d.key === key).map((d) => d.band);
}

export function LayoutPalette({
  layout,
  activeBand,
  selectedId,
  templateName,
  onTemplateNameChange,
  onBandChange,
}: Props) {
  const placedKeys = React.useMemo(() => {
    const set = new Set<string>();
    for (const e of layout.header.elements) set.add(`header:${e.key}`);
    for (const e of layout.footer.elements) set.add(`footer:${e.key}`);
    return set;
  }, [layout]);

  const selectedKey = React.useMemo(() => {
    if (!selectedId) return null;
    const h = layout.header.elements.find((e) => e.id === selectedId);
    if (h) return `header:${h.key}`;
    const f = layout.footer.elements.find((e) => e.id === selectedId);
    if (f) return `footer:${f.key}`;
    return null;
  }, [layout, selectedId]);

  const renderFieldRow = (def: LayoutFieldDef) => {
    const placedKey = `${def.band}:${def.key}`;
    if (!MULTI_FIELD_KEYS.has(def.key) && placedKeys.has(placedKey)) return null;

    const isSelected = selectedKey === placedKey;

    return (
      <li key={`${def.band}-${def.key}`}>
        <button
          type="button"
          className={navBtnClass(isSelected)}
          draggable
          onDragStart={(e) => {
            onBandChange(def.band);
            e.dataTransfer.setData(
              'application/x-layout-palette',
              JSON.stringify({ kind: def.kind, key: def.key as LayoutElementKey, band: def.band }),
            );
            e.dataTransfer.effectAllowed = 'copy';
          }}
          style={{ cursor: 'grab' }}
          title={
            def.key === 'orderNumber'
              ? `${def.label} — auf die Vorlage ziehen (nur auf Rechnungen sichtbar)`
              : MULTI_FIELD_KEYS.has(def.key)
                ? `${def.label} — auf die Vorlage ziehen (mehrfach möglich)`
                : def.required
                  ? `${def.label} (Pflichtfeld) — auf die Vorlage ziehen`
                  : `${def.label} — auf die Vorlage ziehen`
          }
        >
          {FIELD_ICONS[def.key] ?? <FileText className="h-4 w-4 shrink-0" strokeWidth={1.75} />}
          <span className="min-w-0 flex-1 truncate">{def.label}</span>
          {def.required ? (
            <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={1.75} />
          ) : null}
        </button>
      </li>
    );
  };

  const renderDesignRow = (def: LayoutFieldDef) => {
    // Design elements stay in the palette — each one may be placed any number of times.
    const allowed = bandsForKey(def.key);
    const dragBand: LayoutBand = allowed.includes(activeBand) ? activeBand : allowed[0] ?? 'header';
    const isSelected = !!selectedKey && selectedKey.endsWith(`:${def.key}`);

    if (def.key === 'divider') {
      const lineEntries: Array<{
        orientation: 'horizontal' | 'vertical';
        label: string;
        title: string;
      }> = [
        { orientation: 'horizontal', label: 'Horizontale Linie', title: 'Horizontale Linie — in Kopf- oder Fußbereich ziehen' },
        { orientation: 'vertical', label: 'Vertikale Linie', title: 'Vertikale Linie — in Kopf- oder Fußbereich ziehen' },
      ];
      return (
        <React.Fragment key="design-divider">
          {lineEntries.map((entry) => (
            <li key={`design-divider-${entry.orientation}`}>
              <button
                type="button"
                className={navBtnClass(isSelected && (
                  entry.orientation === 'vertical'
                    ? layout.header.elements.concat(layout.footer.elements).some(
                        (e) => e.id === selectedId && e.key === 'divider' && e.lineOrientation === 'vertical',
                      )
                    : layout.header.elements.concat(layout.footer.elements).some(
                        (e) => e.id === selectedId && e.key === 'divider' && e.lineOrientation !== 'vertical',
                      )
                ))}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(
                    'application/x-layout-palette',
                    JSON.stringify({
                      kind: 'design',
                      key: 'divider',
                      band: dragBand,
                      lineOrientation: entry.orientation,
                    }),
                  );
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                style={{ cursor: 'grab' }}
                title={entry.title}
              >
                <Minus
                  className={`h-4 w-4 shrink-0 ${entry.orientation === 'vertical' ? 'rotate-90' : ''}`}
                  strokeWidth={1.75}
                />
                <span className="min-w-0 flex-1 truncate">{entry.label}</span>
              </button>
            </li>
          ))}
        </React.Fragment>
      );
    }

    return (
      <li key={`design-${def.key}`}>
        <button
          type="button"
          className={navBtnClass(isSelected)}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData(
              'application/x-layout-palette',
              JSON.stringify({
                kind: 'design',
                key: def.key as LayoutElementKey,
                band: dragBand,
              }),
            );
            e.dataTransfer.effectAllowed = 'copy';
          }}
          style={{ cursor: 'grab' }}
          title={
            allowed.length > 1
              ? `${def.label} — in Kopf- oder Fußbereich ziehen`
              : `${def.label} — auf die Vorlage ziehen`
          }
        >
          {FIELD_ICONS[def.key] ?? <FileText className="h-4 w-4 shrink-0" strokeWidth={1.75} />}
          <span className="min-w-0 flex-1 truncate">{def.label}</span>
        </button>
      </li>
    );
  };

  const renderBandFields = (band: LayoutBand) => {
    const fields = LAYOUT_FIELD_DEFS.filter((d) => d.band === band && d.kind === 'field');
    const rows = fields.map(renderFieldRow).filter(Boolean);
    const isActive = activeBand === band;
    const title = band === 'header' ? 'Kopfbereich' : 'Fußbereich';
    const Icon = band === 'header' ? LayoutTemplate : CreditCard;

    return (
      <div key={band} className={band === 'footer' ? 'mt-6' : 'mt-1'}>
        <button
          type="button"
          className="mx-1 mb-1 flex w-[calc(100%-0.5rem)] items-center gap-2 rounded-md px-2 py-1.5 text-left text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted transition-colors"
          onClick={() => onBandChange(band)}
        >
          <Icon className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />
          <span className={`text-sm ${isActive ? 'font-semibold text-app-text-muted' : 'font-medium'}`}>
            {title}
          </span>
        </button>
        {rows.length > 0 ? (
          <ul className="pl-1.5">{rows}</ul>
        ) : (
          <p className="px-3 py-1 text-xs text-slate-400">Alle Felder platziert</p>
        )}
      </div>
    );
  };

  const designRows = DESIGN_DEFS.map(renderDesignRow).filter(Boolean);

  return (
    <aside className="flex w-[272px] shrink-0 flex-col overflow-hidden border-r border-app-border bg-app-surface self-stretch">
      <div className="px-4 pt-5 pb-3 border-b border-app-border shrink-0">
        <h2 className="text-lg font-bold text-app-text tracking-tight">Elemente</h2>
        <p className={`mt-3 ${SETTINGS_FIELD_LABEL}`}>Titel der Vorlage</p>
        <input
          className={`${SETTINGS_INPUT_CLASS} mt-1.5`}
          value={templateName}
          onChange={(e) => onTemplateNameChange(e.target.value)}
          aria-label="Titel der Rechnungsvorlage"
          placeholder="z. B. Standard-Layout"
        />
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain app-scrollbar px-2 pt-2 pb-6">
        {renderBandFields('header')}
        <div className="mx-3 mt-6 border-t border-app-border" />
        {renderBandFields('footer')}

        <div className="mx-3 mt-6 border-t border-app-border" />
        <div className="mt-1">
          <div className="mx-1 mb-1 flex w-[calc(100%-0.5rem)] items-center gap-2 rounded-md px-2 py-1.5 text-app-text-subtle">
            <Palette className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />
            <span className="text-sm font-semibold text-app-text-muted">Design-Elemente</span>
          </div>
          <ul className="pl-1.5">{designRows}</ul>
        </div>

        <div className="mx-3 mt-6 border-t border-app-border" />
        <p className="px-3 pt-4 text-xs leading-relaxed text-app-text-subtle">
          Fehlende Felder und Design-Elemente auf die Vorlage ziehen. Design-Elemente lassen sich
          beliebig oft platzieren; Firmenlogo, Bild, Freitext, Linien und Hintergrundfläche
          zusätzlich in Kopf- und Fußbereich.
        </p>
      </nav>
    </aside>
  );
}
